const STORAGE_KEY = 'overcut:welcome-popup';
const ARRIVEE_KEY = 'overcut:welcome-popup-arrivee';

// Part de la page a partir de laquelle on considere que le visiteur lit
// vraiment (reglage « Defilement avant ouverture », 30 % par defaut). Un
// minuteur seul ouvre le popup au nez de quelqu un qui vient d arriver.
const SEUIL_DEFILEMENT_DEFAUT = 30;

/**
 * Welcome popup shown once per visitor.
 *
 * Deux conditions, toutes deux necessaires : un temps passe sur le site
 * (compte depuis l arrivee, pas depuis chaque page, pour qu un visiteur qui
 * parcourt plusieurs pages ne reparte pas de zero) et un defilement minimum
 * de la page en cours (signal d interet).
 *
 * Shopify's `{% form 'customer' %}` posts with a full page reload, so the
 * success state is rendered server-side and detected here: when the markup
 * comes back carrying the success block, the popup reopens straight away with
 * the code instead of waiting out the delay again.
 */
class OvercutWelcomePopup extends HTMLElement {
  /** @type {number} */
  #timer = 0;

  /** @type {(() => void) | null} */
  #onScroll = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.panel = this.querySelector('[data-popup-panel]');
    this.close = this.close.bind(this);

    this.querySelectorAll('[data-popup-close]').forEach((node) => node.addEventListener('click', this.close));
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    // Covers the Escape key too, which closes a <dialog> without our handler.
    this.dialog.addEventListener('close', () => this.remember());

    const code = this.querySelector('[data-copy-code]');
    if (code) code.addEventListener('click', () => this.copier(code));

    if (this.querySelector('[data-popup-success]')) {
      this.open();
      return;
    }

    // In the theme editor, always preview it rather than honouring a past dismissal.
    if (this.dataset.designMode !== 'true' && this.seen()) return;

    this.#armer();
  }

  disconnectedCallback() {
    this.#desarmer();
  }

  #armer() {
    const delay = Number(this.dataset.delay || 90) * 1000;
    const seuil = (Number(this.dataset.scroll) || SEUIL_DEFILEMENT_DEFAUT) / 100;
    // Les deux conditions doivent etre remplies : le temps ecoule ET le
    // defilement atteint, dans n importe quel ordre.
    let tempsEcoule = false;
    let assezDefile = false;
    const verifier = () => {
      if (tempsEcoule && assezDefile) this.open();
    };

    this.#onScroll = () => {
      const doc = document.documentElement;
      const hauteur = doc.scrollHeight - window.innerHeight;
      // Page trop courte pour defiler : la condition de defilement est acquise.
      if (hauteur <= 0 || doc.scrollTop / hauteur >= seuil) {
        assezDefile = true;
        window.removeEventListener('scroll', this.#onScroll);
        verifier();
      }
    };
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#onScroll();

    // Heure d arrivee sur le site, gardee pour la session (onglet) en cours.
    let arrivee = Date.now();
    try {
      arrivee = Number(sessionStorage.getItem(ARRIVEE_KEY)) || arrivee;
      sessionStorage.setItem(ARRIVEE_KEY, String(arrivee));
    } catch {
      /* stockage indisponible : le temps se compte depuis cette page */
    }
    this.#timer = window.setTimeout(() => {
      tempsEcoule = true;
      verifier();
    }, Math.max(0, delay - (Date.now() - arrivee)));
  }

  #desarmer() {
    clearTimeout(this.#timer);
    if (this.#onScroll) {
      window.removeEventListener('scroll', this.#onScroll);
      this.#onScroll = null;
    }
  }

  seen() {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'done';
    } catch {
      // Private browsing: treat as unseen rather than throwing.
      return false;
    }
  }

  remember() {
    try {
      localStorage.setItem(STORAGE_KEY, 'done');
    } catch {
      /* storage unavailable, popup will simply show again next visit */
    }
  }

  /** @param {HTMLElement} bouton */
  async copier(bouton) {
    const code = bouton.dataset.copyCode || '';
    const indice = bouton.querySelector('[data-copy-hint]');
    let copie = false;

    try {
      await navigator.clipboard.writeText(code);
      copie = true;
    } catch {
      // Presse-papiers refuse (http, permission, vieux navigateur) : on
      // selectionne le code pour que « copier » reste a un geste, au lieu de
      // laisser le visiteur viser les lettres une par une.
      const valeur = bouton.querySelector('.overcut-welcome__code-value');
      const selection = window.getSelection();
      if (valeur && selection) {
        const plage = document.createRange();
        plage.selectNodeContents(valeur);
        selection.removeAllRanges();
        selection.addRange(plage);
      }
    }

    if (!copie || !indice) return;

    bouton.dataset.copied = 'true';
    indice.textContent = indice.dataset.labelCopied || indice.textContent;
    // Revenir a l invitation : le visiteur peut vouloir recopier plus tard,
    // et un « copié » figé ne dit plus rien.
    window.setTimeout(() => {
      delete bouton.dataset.copied;
      indice.textContent = indice.dataset.labelCopy || '';
    }, 2500);
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    // Le minuteur et l ecouteur sont desormais inutiles : le popup ne s ouvre
    // qu une fois, et un ecouteur de defilement laisse en place tournerait
    // pour rien a chaque geste du visiteur.
    this.#desarmer();

    this.dialog.showModal();

    // showModal() pose le focus sur le premier element focusable, la croix,
    // dont l anneau de focus se dessine comme un cadre parasite en haut a
    // droite. Le panneau porte tabindex="-1" pour le recevoir a la place.
    this.panel?.focus({ preventScroll: true });

    // Memoriser des l AFFICHAGE, pas seulement a la fermeture. Un visiteur qui
    // ignorait le popup et rechargeait, ou passait a une autre page, le revoyait
    // toutes les 15 secondes : la memoire n etait ecrite qu au clic sur fermer.
    this.remember();
  }

  close() {
    this.remember();
    this.dialog?.close();
  }
}

if (!customElements.get('overcut-welcome-popup')) {
  customElements.define('overcut-welcome-popup', OvercutWelcomePopup);
}
